import {createContext, useContext, useEffect, useReducer} from 'react';
import FinishSound from '../sounds/finished.flac';
import {db} from "../db";

export const GenerateContext = createContext(null);
export const GenerateDispatchContext = createContext(null);

export function GenerateProvider({children}) {
    const [generateStatus, dispatch] = useReducer(generateReducer, {
        uid: '',
        loading: false,
        status: 'ready',
        progress: 0,
        message: "",
        tag: "",
        intervalId: null,
        serverStatus: {},
        abortControllers: [],
        totalImages: 0,
        failedImage: false,
        failedImageID: null,
        failedWorkloadUID: null,
        undeliveredImages : false,
        checkingUndeliveredImages: false,
        retrievingUndeliveredImages: false,
        totalUndeliveredImages: 0,
        totalRetrievedImages: 0,
        cancellingUndeliveredCheck: false
    });
    useEffect(() => {
        return () => clearInterval(generateStatus.intervalId);
    }, []);

    return (
        <GenerateContext.Provider value={generateStatus}>
            <GenerateDispatchContext.Provider value={dispatch}>
                {children}
            </GenerateDispatchContext.Provider>
        </GenerateContext.Provider>
    );
}

function abortAllControllers(abortControllers) {
    if (abortControllers.length > 0) {
        abortControllers.forEach(controller => controller.abort());
    }
}

function generateReducer(generateStatus, action) {
    console.log("GenerateContext: ", generateStatus, action);
    switch(action.type) {
        case 'loading': {
            return {...generateStatus, loading: true, status: 'loading', message: "", intervalId: action.intervalId};
        }
        case 'generate': {
            localStorage.setItem('remember_workload', JSON.stringify({
                active: true,
                uid: action.uid,
                count: action.count,
                totalImage: 0,
                num: 0,
            }))
            return {...generateStatus, uid: action.uid, status: 'generating', message: "", count: action.count, totalImages: 0};
        }
        case 'cancel': {
            abortAllControllers(generateStatus.abortControllers);
            clearInterval(generateStatus.intervalId);
            return {...generateStatus, loading: false, status: 'canceling', progress: 0, intervalId: null, serverStatus: {}, abortControllers: [], failedImage: false, failedImageID: null, failedWorkloadUID: null, undeliveredImages: false, checkingUndeliveredImages: false, retrievingUndeliveredImages: false};
        }
        case 'addAbortController': {
            return {...generateStatus, abortControllers: [...generateStatus.abortControllers, action.abortController]};
        }
        case 'progress': {
            return {...generateStatus, progress: action.progress, message: ""};
        }
        case 'imageProgress': {
            // console.log("Image Progress: ", action.setting);
            if (action.setting) {
                const successAudio = new Audio(FinishSound);
                successAudio.loop = false;
                successAudio.volume = 0.4;
                successAudio.play();
            }

            return {...generateStatus, totalImages: generateStatus.totalImages + 1};
        }
        case 'setModels': {
            return {...generateStatus};
        }
        case 'setServerStatus': {
            return {...generateStatus, serverStatus: action.serverStatus};
        }
        case 'clearError': {
            return {...generateStatus, message: ""};
        }
        case 'ready': {
            abortAllControllers(generateStatus.abortControllers);

            const workloadDetails = JSON.parse(localStorage.getItem("remember_workload"))
            if (workloadDetails.active) {
                localStorage.setItem('remember_workload', JSON.stringify({
                    active: false,
                    uid: "",
                    count: 0,
                    totalImage: 0,
                    num: 0,
                }))
            }

            if (generateStatus.intervalId) {
                clearInterval(generateStatus.intervalId);
            }
            return {uid: '', loading: false, status: 'ready', progress: 0, message: "", count: 0, totalImages: 0, intervalId: null, serverStatus: {}, abortControllers: [], failedImage: false, failedImageID: null, failedWorkloadUID: null, undeliveredImages: false, checkingUndeliveredImages: false, retrievingUndeliveredImages: false};
        }
        case 'done': {
            abortAllControllers(generateStatus.abortControllers);

            const workloadDetails = JSON.parse(localStorage.getItem("remember_workload"))
            if (workloadDetails.active) {
                localStorage.setItem('remember_workload', JSON.stringify({
                    active: false,
                    uid: "",
                    count: 0,
                    totalImage: 0,
                    num: 0,
                }))
            }

            if (generateStatus.intervalId) {
                clearInterval(generateStatus.intervalId);
            }
            return {uid: '', loading: false, status: 'done', progress: 0, message: "", count: 0, totalImages: 0, intervalId: null, serverStatus: {}, abortControllers: [], failedImage: false, failedImageID: null, failedWorkloadUID: null, undeliveredImages: false, checkingUndeliveredImages: false, retrievingUndeliveredImages: false};
        }
        case 'tagging': {
            return {...generateStatus, tag: action.tag}
        }
        case 'error': {
            abortAllControllers(generateStatus.abortControllers);


            if (generateStatus.intervalId) {
                clearInterval(generateStatus.intervalId);
            }
            return {uid: '', loading: false, status: 'error', progress: 0, message: action.message, count: 0, totalImages: 0, intervalId: null, serverStatus: {}, abortControllers: [], failedImage: false, failedImageID: null, failedWorkloadUID: null, undeliveredImages: false, checkingUndeliveredImages: false, retrievingUndeliveredImages: false};
        }
        case 'failedImage': {
            return {...generateStatus, failedImage: true};
        }
        case 'failedImageID': {
            return {...generateStatus, failedImageID: action.failedImageID};
        }
        case 'failedWorkloadUID': {
            return {...generateStatus, failedWorkloadUID: action.failedWorkloadUID};
        }
        case 'undeliveredImages': {
            return {...generateStatus, undeliveredImages: true, checkingUndeliveredImages: false, retrievingUndeliveredImages: false};
        }
        case 'checkingUndeliveredImages': {
            return {...generateStatus, checkingUndeliveredImages: true, retrievingUndeliveredImages: false};
        }
        case 'retrievingUndeliveredImages': {
            return {...generateStatus, retrievingUndeliveredImages: true, checkingUndeliveredImages: false};
        }
        case 'totalUndeliveredImages': {
            return {...generateStatus, totalUndeliveredImages: action.totalUndeliveredImages, totalRetrievedImages: 0};
        }
        case 'totalRetrievedImages': {
            return {...generateStatus, totalRetrievedImages: generateStatus.totalRetrievedImages + 1};
        }
        case 'cancellingUndeliveredCheck': {
            return {...generateStatus, cancellingUndeliveredCheck: true};
        }
        case 'resetcancellingUndeliveredCheck': {
            return {...generateStatus, cancellingUndeliveredCheck: false};
        }
        default: {
            return generateStatus;
        }
    }
}

export function useGenerateStatus() {
    return useContext(GenerateContext);
}

export function useGenerateDispatch() {
    return useContext(GenerateDispatchContext);
}
